import { variablesGlobales } from "./variables";
import { getAttributionContext } from "../services/attribution/attribution";

// Rutas con tokens que no deben llegar a Umami (como ruta o como URL completa).
const SENSITIVE_PATHS = [/^((?:[a-z]+:\/\/[^/]+)?\/veriff(?:-status)?\/)[^/?#]+/i];

// Evento con el que se repite el último toque de campaña en la sesión
// identificada. El backend lo lee al atribuir (UmamiAnalyticsReferralLookup).
export const ATTRIBUTION_EVENT = "atribucion";
// Misma ventana que el backend (UMAMI_LOOKBACK_DAYS).
const ATTRIBUTION_WINDOW_DAYS = 30;
// Último toque ya enviado, para no repetirlo en cada recarga.
const REPORTED_TOUCH_KEY = "glv_lt_umami";
const CANAL_DIRECTO = "directo";
const MAX_TAG_LENGTH = 50;

let umamiReady = null;

export function maskSensitiveUrl(url) {
    if (typeof url !== "string") return url;
    return SENSITIVE_PATHS.reduce((masked, pattern) => masked.replace(pattern, "$1:token"), url);
}

function isWithinWindow(touch, now) {
    const capturedAt = new Date(touch?.capturedAt).getTime();
    return !Number.isNaN(capturedAt) && now.getTime() - capturedAt <= ATTRIBUTION_WINDOW_DAYS * 86400 * 1000;
}

/**
 * Canal del visitante según su último toque de campaña en la ventana. Viaja en
 * el campo tag de Umami, que se puede filtrar en embudos, objetivos e ingresos.
 * Los enlaces de referidos llevan el documento de quien invita: nunca se envía.
 */
export function canalDeToque(touch, now = new Date()) {
    if (!touch || !isWithinWindow(touch, now)) return CANAL_DIRECTO;
    if (touch.utmId?.startsWith("referido")) return "referido";
    const { gclid, fbclid, ttclid } = touch.clickIds || {};
    const canal = touch.utmId || touch.utm?.source || (gclid && "google") || (ttclid && "tiktok") || (fbclid && "meta");
    if (!canal) return CANAL_DIRECTO;
    return String(canal).toLowerCase().replace(/[^a-z0-9_-]/g, "_").slice(0, MAX_TAG_LENGTH);
}

// Umami filtra embudos y objetivos mirando cada evento, y la sesión identificada
// no pasa por la URL de campaña: los eventos llevan las UTMs del último toque.
function withCampaign(url, payload, touch, now) {
    if (!payload?.name || typeof url !== "string" || url.includes("?")) return url;
    if (!touch?.utm || !isWithinWindow(touch, now)) return url;
    const params = new URLSearchParams();
    Object.entries(touch.utm).forEach(([key, value]) => params.set(`utm_${key}`, value));
    const query = params.toString();
    return query ? `${url}?${query}` : url;
}

// Umami llama esta función antes de cada envío (data-before-send).
export function beforeSend(type, payload, now = new Date()) {
    const masked = {
        ...payload,
        url: maskSensitiveUrl(payload?.url),
        referrer: maskSensitiveUrl(payload?.referrer),
    };
    try {
        const { lastTouch } = getAttributionContext(now);
        return {
            ...masked,
            url: withCampaign(masked.url, payload, lastTouch, now),
            tag: canalDeToque(lastTouch, now),
        };
    } catch (error) {
        return masked;
    }
}

function readLocal(key) {
    try {
        return window.localStorage.getItem(key);
    } catch (error) {
        return null;
    }
}

function writeLocal(key, value) {
    try {
        window.localStorage.setItem(key, value);
    } catch (error) {
        // localStorage no disponible.
    }
}

/**
 * Carga el tracker de Umami. El distinct ID es el documento si el cliente ya
 * se identificó en este navegador y, si no, el ID de visitante anónimo.
 */
export function loadUmami({ visitorId } = {}) {
    const { umamiScriptUrl, umamiWebsiteId, umamiDomains } = variablesGlobales;
    if (!umamiWebsiteId || typeof document === "undefined") return;
    if (document.querySelector("script[data-website-id]")) return;

    window.galileaUmamiBeforeSend = beforeSend;

    const script = document.createElement("script");
    script.defer = true;
    script.src = umamiScriptUrl;
    script.setAttribute("data-website-id", umamiWebsiteId);
    script.setAttribute("data-before-send", "galileaUmamiBeforeSend");
    const distinctId = readLocal("doc") || visitorId;
    if (distinctId) script.setAttribute("data-distinct-id", distinctId);
    if (umamiDomains) script.setAttribute("data-domains", umamiDomains);

    umamiReady = new Promise((resolve, reject) => {
        script.onload = resolve;
        script.onerror = reject;
    });
    document.head.appendChild(script);
}

/** Se resuelve cuando el tracker está disponible (falla si un bloqueador lo impide). */
export function whenUmamiReady() {
    if (typeof window !== "undefined" && window.umami) return Promise.resolve();
    return umamiReady || Promise.reject(new Error("umami-not-loaded"));
}

/**
 * URL de aterrizaje del toque con sus parámetros de campaña. Umami guarda solo
 * los primeros 500 caracteres de la query, así que van primero los datos que el
 * backend necesita para atribuir. Las UTMs tienen además columnas propias.
 */
export function buildTouchUrl(touch, origin = "") {
    const params = new URLSearchParams();
    if (touch?.utmId) params.set("utm_id", touch.utmId);
    if (touch?.partnerId) params.set("partnerId", touch.partnerId);
    Object.entries(touch?.clickIds || {}).forEach(([key, value]) => params.set(key, value));
    Object.entries(touch?.utm || {}).forEach(([key, value]) => params.set(`utm_${key}`, value));
    return `${origin}${touch?.landingPath || "/"}?${params.toString()}`;
}

export function shouldReportTouch(touch, now = new Date()) {
    if (!touch?.paid) return false;
    const capturedAt = new Date(touch.capturedAt).getTime();
    return !Number.isNaN(capturedAt) && now.getTime() - capturedAt <= ATTRIBUTION_WINDOW_DAYS * 86400 * 1000;
}

/**
 * Identifica al cliente en Umami con su documento y repite en esa sesión el
 * último toque de campaña pagado. Umami crea una sesión nueva al identificar
 * (el ID de sesión incluye el distinct ID), así que las páginas vistas
 * anónimas con las UTMs no quedan unidas al documento.
 */
export async function identifyInUmami(doc, lastTouch, now = new Date()) {
    const umami = window.umami;
    if (!doc || !umami?.identify) return false;

    await umami.identify({ id: doc });
    if (!shouldReportTouch(lastTouch, now)) return true;

    const marker = `${doc.slice(-4)}|${lastTouch.capturedAt}`;
    if (readLocal(REPORTED_TOUCH_KEY) === marker) return true;

    await umami.track((payload) => ({
        ...payload,
        name: ATTRIBUTION_EVENT,
        url: buildTouchUrl(lastTouch, window.location.origin),
        data: { capturado: lastTouch.capturedAt },
    }));
    writeLocal(REPORTED_TOUCH_KEY, marker);
    return true;
}
